import { FormEvent, useContext, useRef, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { ADMIN_API_URL } from "../../utils/env";
import { aevoUnits, formatAEVO } from "./budget";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { MakerLaunchPolicy, fractionPercent } from "./LaunchPolicy";

const allocationIDs = [
  "BTC-PERP",
  "ETH-PERP",
  "BTC-OPTIONS/expiry-1",
  "BTC-OPTIONS/expiry-2",
  "ETH-OPTIONS/expiry-1",
  "ETH-OPTIONS/expiry-2",
];

interface LaunchBudget {
  policy_version: string;
  epoch: number;
  pool_units: string;
  maker_pool_units: string;
  trading_base_units: string;
  allocation_rounding_to_trading_units: string;
  allocations: Record<string, string>;
}

interface AllocationPreview {
  id: string;
  token_units: string;
  paid_units: string;
  returned_units: string;
  unallocated_units: string;
  cap_excess: string;
  dust: string;
  awards: {
    account_group: string;
    units: string;
    uncapped: string;
    cap_excess: string;
    dust: string;
  }[];
}

interface HourlyProgress {
  HourStart: string;
  Expected: number;
  Observed: number;
  Missing: number;
  Present: number;
  Presence: string;
  Qualified: boolean;
  Score: string;
}

interface AccountProgress {
  Account: string;
  Score: string;
  EligibleScore: string;
  VolatileObserved?: number;
  VolatilePresent?: number;
  VolatilePresence?: string;
  VolatilityRequirementApplied?: boolean;
  VolatilityQualified?: boolean;
  CommitmentMinimumDepthUSD?: string;
  Eligible: boolean;
  Hours: HourlyProgress[];
  QualifiedHours: number;
  UnqualifiedHours: number;
}

export function Collection({
  ready,
  launchPolicy,
}: {
  ready: boolean;
  launchPolicy?: MakerLaunchPolicy;
}) {
  const { queryConfig, userEmail, tokenId } = useContext(AuthContext);
  const storageKey = `aevo:maker-week:pending:${
    process.env.REACT_APP_ENVIRONMENT
  }:${userEmail || tokenId || ""}`;
  const [pending, setPending] = useState<any>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey) || "null");
    } catch {
      return undefined;
    }
  });
  const [review, setReview] = useState<any>();
  const [recorded, setRecorded] = useState("");
  const lock = useRef(false);
  const remember = (value?: any) => {
    setPending(value);
    try {
      if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(storageKey);
    } catch {
      /* Keep in-memory retries. */
    }
  };
  const inspectLedger = async (item: any) => {
    const { data } = await axios.get(ADMIN_API_URL("makers/state"), {
      ...queryConfig(),
      timeout: 20000,
    });
    if (data.environment !== "testnet" || data.testnet_only !== true)
      throw new Error("Invalid testnet ledger response.");
    const week = data.weekly_ledger?.weeks?.[item.command.payload.week_id];
    if (!week) return false;
    if (
      week.fingerprint !== item.manifest.fingerprint ||
      week.input_hash !== item.command.payload.input_hash ||
      week.policy_hash !== item.command.payload.policy_hash
    )
      throw new Error(
        "This week has a different recorded manifest. Stop and reconcile the ledger."
      );
    setRecorded(item.command.payload.week_id);
    remember(undefined);
    setReview(undefined);
    return true;
  };
  const prepareRecord = () => {
    setError("");
    setRecorded("");
    try {
      if (
        !ready ||
        pending ||
        launchPolicy?.reward_settlement_ready !== true ||
        preview?.launch_policy?.reward_settlement_ready !== true ||
        preview?.distributable !== true ||
        preview?.settlement_blockers?.length !== 0 ||
        !preview?.commit_payload ||
        !/^[0-9a-f]{64}$/.test(preview.manifest?.fingerprint || "")
      )
        throw new Error(
          "A distributable testnet preview and reconciliation of pending changes are required."
        );
      const serverTime = Date.parse(preview.server_time);
      if (!Number.isFinite(serverTime))
        throw new Error("Refresh preview to obtain server time.");
      const nonce = Array.from(
        crypto.getRandomValues(new Uint8Array(16)),
        (n) => n.toString(16).padStart(2, "0")
      ).join("");
      setReview(
        JSON.parse(
          JSON.stringify({
            command: {
              operation: "settle_week",
              request_id: `makerv1:${
                Math.floor(serverTime / 1000) + 3600
              }:${nonce}`,
              payload: preview.commit_payload,
            },
            manifest: preview.manifest,
          })
        )
      );
    } catch (requestError) {
      setError(affiliateError(requestError));
    }
  };
  const record = async (item: any, refreshOnly = false) => {
    if (!ready || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      if (await inspectLedger(item)) return;
      if (refreshOnly) {
        setError(
          "Not yet recorded. Retry the identical request; no payment has been made."
        );
        return;
      }
      remember(item);
      const { data } = await axios.post(
        ADMIN_API_URL("makers/command"),
        item.command,
        { ...queryConfig(), timeout: 20000 }
      );
      if (
        data.environment !== "testnet" ||
        data.testnet_only !== true ||
        data.success !== true ||
        data.request_id !== item.command.request_id
      )
        throw new Error(
          "Unconfirmed accounting command. Reconcile the ledger or retry the identical request."
        );
      if (!(await inspectLedger(item)))
        setError(
          "Command acknowledged; awaiting the persisted ledger. Refresh the ledger to confirm."
        );
    } catch (e) {
      const response = (e as any)?.response;
      if (
        [400, 422].includes(response?.status) &&
        response.data?.environment === "testnet" &&
        response.data?.testnet_only === true &&
        response.data?.request_id === item.command.request_id &&
        typeof response.data?.error === "string"
      ) {
        try {
          if (await inspectLedger(item)) return;
          // A rejected expired request can be prepared again. Unlike member
          // versions, the immutable week identity prevents duplicate awards
          // even if the earlier request is not visible on this reader yet.
          remember(undefined);
          setReview(undefined);
        } catch (reconcileError) {
          // Preserve the exact request if the ledger cannot be reconciled.
          setError(affiliateError(reconcileError));
          return;
        }
      }
      setError(affiliateError(e));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const [account, setAccount] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [result, setResult] = useState<any>();
  const [error, setError] = useState("");
  const [policy, setPolicy] = useState("");
  const [weekID, setWeekID] = useState("");
  const [totalBudget, setTotalBudget] = useState("");
  const [launchEpoch, setLaunchEpoch] = useState("1");
  const [allocationID, setAllocationID] = useState("BTC-PERP");
  const invalidate = () => {
    setPreview(undefined);
    setReview(undefined);
  };
  const [preview, setPreview] = useState<any>();
  const canReviewAccounting =
    launchPolicy?.reward_settlement_ready === true &&
    preview?.launch_policy?.reward_settlement_ready === true &&
    preview?.distributable === true &&
    preview?.settlement_blockers?.length === 0 &&
    !!preview?.commit_payload;
  const [busy, setBusy] = useState(false);
  const read = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setError("");
    setResult(undefined);
    setBusy(true);
    try {
      const q = new URLSearchParams({
        account,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        limit: "100",
      });
      const { data } = await axios.get(ADMIN_API_URL(`makers/quotes?${q}`), {
        ...queryConfig(),
        timeout: 20000,
      });
      if (
        data.environment !== "testnet" ||
        data.testnet_only !== true ||
        !Array.isArray(data.samples)
      )
        throw new Error("Invalid testnet collection response");
      setResult(data);
    } catch (requestError) {
      setError(affiliateError(requestError));
    } finally {
      setBusy(false);
    }
  };
  const calculate = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setError("");
    setPreview(undefined);
    setReview(undefined);
    setBusy(true);
    try {
      if (!launchPolicy)
        throw new Error(
          "Refresh the confirmed launch policy before previewing."
        );
      const scoringPolicy = JSON.parse(policy);
      if (scoringPolicy?.Environment !== "testnet")
        throw new Error(
          "Explicit testnet policy is required; participant families are resolved by the server."
        );
      if (
        [
          "AccountGroups",
          "CommitmentMinimumDepthUSD",
          "OptionAllocation",
          "OptionInstruments",
        ].some((field) =>
          Object.prototype.hasOwnProperty.call(scoringPolicy, field)
        )
      )
        throw new Error(
          "Omit authority fields; participant families, agreed depth and options publications are resolved by the server."
        );
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(weekID) ||
        new Date(`${weekID}T00:00:00Z`).toISOString().slice(0, 10) !== weekID
      )
        throw new Error("Choose a valid UTC week start date.");
      const epoch = Number(launchEpoch);
      if (
        !Number.isInteger(epoch) ||
        epoch < 1 ||
        epoch > launchPolicy.initial_epoch_count
      )
        throw new Error(
          "Choose launch epoch 1 through 4. Later epochs require a reviewed policy."
        );
      if (!allocationIDs.includes(allocationID))
        throw new Error("Choose a confirmed launch allocation.");
      const pool = aevoUnits(totalBudget);
      if (pool <= BigInt(0))
        throw new Error("Total weekly budget must be greater than zero.");
      const body = {
        week_id: weekID,
        allocation_id: allocationID,
        pool_units: pool.toString(),
        launch_policy_version: launchPolicy.version,
        launch_epoch: epoch,
        account_grouping_policy: launchPolicy.account_grouping_policy,
        policy: scoringPolicy,
      };
      const { data } = await axios.post(
        ADMIN_API_URL("makers/weekly-preview"),
        body,
        { ...queryConfig(), timeout: 30000 }
      );
      if (
        data.environment !== "testnet" ||
        data.testnet_only !== true ||
        data.preview !== true ||
        data.launch_policy?.version !== launchPolicy.version ||
        data.launch_budget?.policy_version !== launchPolicy.version ||
        data.launch_budget?.epoch !== epoch ||
        data.launch_budget?.pool_units !== pool.toString() ||
        ![
          "pool_units",
          "maker_pool_units",
          "trading_base_units",
          "allocation_rounding_to_trading_units",
        ].every((key) => /^\d+$/.test(data.launch_budget?.[key])) ||
        !data.launch_budget?.allocations ||
        Object.keys(data.launch_budget.allocations).length !== 6 ||
        !allocationIDs.every((id) =>
          /^\d+$/.test(data.launch_budget.allocations[id])
        ) ||
        !Array.isArray(data.settlement_blockers) ||
        !data.settlement_blockers.every(
          (reason: unknown) => typeof reason === "string"
        ) ||
        typeof data.distributable !== "boolean"
      )
        throw new Error("Invalid testnet preview response");
      setPreview(data);
    } catch (requestError) {
      setError(affiliateError(requestError));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <section>
        <h2>Quote collection</h2>
        <p>
          Inspect recorded samples for one explicit wallet. A missing sample is
          different from an observed empty order book.
        </p>
        <form onSubmit={read}>
          <label htmlFor="maker-sample-wallet">
            Sample wallet
            <input
              id="maker-sample-wallet"
              required
              pattern="0x[0-9a-fA-F]{40}"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
            />
          </label>
          <label htmlFor="maker-sample-start-iso-date">
            Sample start (ISO date)
            <input
              id="maker-sample-start-iso-date"
              required
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </label>
          <label htmlFor="maker-sample-end-iso-date">
            Sample end (ISO date)
            <input
              id="maker-sample-end-iso-date"
              required
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </label>
          <button type="submit" disabled={!ready || busy}>
            Load samples
          </button>
        </form>
        {result && (
          <>
            <p>
              {result.samples.length} recorded samples
              {result.truncated
                ? " · More samples exist; narrow the time range."
                : ""}
            </p>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Slot</th>
                    <th>Instrument</th>
                    <th>Sequence</th>
                    <th>Reference mid</th>
                    <th>Orders</th>
                    <th>Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {result.samples.map((s: any) => (
                    <tr key={s.ID}>
                      <td>{s.SlotAt}</td>
                      <td>{s.Instrument}</td>
                      <td>{s.Sequence}</td>
                      <td>{s.Mid}</td>
                      <td>{s.Orders?.length || 0}</td>
                      <td>{s.Gap || "None"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      <section>
        <h2>Weekly reward preview</h2>
        <p>
          Calculate scores and a proposed allocation using an explicit testnet
          policy. This does not publish rewards or move funds.
        </p>
        <p>
          The server reserves 25% for makers using the confirmed options and
          perps allocations. This form previews one allocation within that
          split. Options calculations are configured. A bounded preview cannot
          complete authoritative weekly settlement, activate deployment or
          publish claims.
        </p>
        <form onSubmit={calculate}>
          <label htmlFor="maker-week-starts-utc">
            Week starts (UTC)
            <input
              id="maker-week-starts-utc"
              required
              type="date"
              value={weekID}
              onChange={(e) => {
                setWeekID(e.target.value);
                invalidate();
              }}
            />
          </label>
          <label htmlFor="maker-total-weekly-budget-aevo">
            Total weekly budget (AEVO)
            <input
              id="maker-total-weekly-budget-aevo"
              required
              inputMode="decimal"
              placeholder="Enter approved budget"
              value={totalBudget}
              onChange={(e) => {
                setTotalBudget(e.target.value);
                invalidate();
              }}
            />
          </label>
          <label htmlFor="maker-launch-epoch">
            Launch epoch
            <input
              id="maker-launch-epoch"
              required
              type="number"
              min="1"
              max="4"
              step="1"
              value={launchEpoch}
              onChange={(e) => {
                setLaunchEpoch(e.target.value);
                invalidate();
              }}
            />
          </label>
          <label htmlFor="maker-market-allocation">
            Market allocation
            <select
              id="maker-market-allocation"
              required
              value={allocationID}
              onChange={(e) => {
                setAllocationID(e.target.value);
                invalidate();
              }}
            >
              {allocationIDs.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </label>
          <div style={{ width: "100%" }}>
            <p>
              Maker share:{" "}
              {launchPolicy
                ? fractionPercent(launchPolicy.maker_pool_fraction)
                : "Awaiting server policy"}
              . All six budgets, including two expiries per options underlying,
              are calculated by the server. Unscored allocations are not
              finalized by a single-market preview.
            </p>
          </div>
          <details style={{ width: "100%" }} open>
            <summary>Advanced scoring policy</summary>
            <p>
              Paste the reviewed WeekPolicy JSON with Environment set to testnet
              and an explicit bounded sampling window. The server resolves
              participant families, approved commitments and active options
              instruments from captured publications. Omit AccountGroups,
              CommitmentMinimumDepthUSD, OptionAllocation and OptionInstruments.
              For options, Instrument may be 0. The family cap applies per
              allocation.
            </p>
            <label htmlFor="maker-reviewed-scoring-policy-json">
              Reviewed scoring policy (JSON)
              <textarea
                id="maker-reviewed-scoring-policy-json"
                required
                rows={10}
                value={policy}
                onChange={(e) => {
                  setPolicy(e.target.value);
                  invalidate();
                }}
              />
            </label>
          </details>
          <button
            type="submit"
            disabled={!ready || !launchPolicy || busy || !!pending}
          >
            Calculate preview
          </button>
        </form>
        {recorded && (
          <p role="status">
            Accounting recorded for {recorded}. Verified in the persisted
            ledger. No payment or claim was published.
          </p>
        )}
        {pending && (
          <div>
            <h3>Unconfirmed accounting change</h3>
            <p>
              Keep this exact request until the ledger confirms the week.
              Retrying does not create another allocation.
            </p>
            <pre>{JSON.stringify(pending.command, null, 2)}</pre>
            <button
              type="button"
              disabled={!ready || busy}
              onClick={() => record(pending, true)}
            >
              Refresh weekly ledger
            </button>
            <button
              type="button"
              disabled={!ready || busy}
              onClick={() => record(pending)}
            >
              Retry identical accounting request
            </button>
          </div>
        )}
        {preview && (
          <details open>
            <summary>Unpublished preview</summary>
            <LaunchBudgetSummary budget={preview.launch_budget} />
            {preview.allocation_preview && (
              <AllocationSummary allocation={preview.allocation_preview} />
            )}
            {!!preview.scores?.Progress?.length && (
              <HourlyPresence
                progress={preview.scores.Progress}
                policy={launchPolicy}
              />
            )}
            <h3>Settlement status</h3>
            <p role={canReviewAccounting ? undefined : "status"}>
              {canReviewAccounting
                ? "Ready for accounting review. No payment or claim has been published."
                : "Blocked · this preview cannot be recorded or distributed."}
            </p>
            <ul>
              {preview.settlement_blockers.map((reason: string) => (
                <li key={reason}>
                  {[
                    ...(launchPolicy?.operational_blockers || []),
                    ...(launchPolicy?.pending_decisions || []),
                  ].find((decision) => decision.code === reason)?.description ||
                    reason.replace(/_/g, " ")}
                </li>
              ))}
            </ul>
            {canReviewAccounting && (
              <RewardSummary
                payload={preview.commit_payload}
                manifest={preview.manifest}
              />
            )}
            <details>
              <summary>Technical preview details</summary>
              <pre>{JSON.stringify(preview, null, 2)}</pre>
            </details>
            <button
              type="button"
              disabled={!ready || busy || !!pending || !canReviewAccounting}
              onClick={prepareRecord}
            >
              Review accounting entry
            </button>
          </details>
        )}
        {review && !pending && (
          <div>
            <h3>Review immutable weekly accounting</h3>
            <p>
              This records the reviewed allocation only. It does not transfer
              AEVO, publish a claim root or mark anyone paid. Requires maker
              rewards write permission.
            </p>
            <RewardSummary
              payload={review.command.payload}
              manifest={review.manifest}
            />
            <details>
              <summary>Exact accounting payload</summary>
              <pre>{JSON.stringify(review.command.payload, null, 2)}</pre>
            </details>
            <p>Manifest fingerprint: {review.manifest.fingerprint}</p>
            <button
              type="button"
              disabled={
                !ready || busy || launchPolicy?.reward_settlement_ready !== true
              }
              onClick={() => record(review)}
            >
              Record testnet accounting
            </button>
          </div>
        )}
      </section>
      {error && <p role="alert">{error}</p>}
    </>
  );
}

function LaunchBudgetSummary({ budget }: { budget: LaunchBudget }) {
  return (
    <div>
      <h3>Server-calculated launch budgets · epoch {budget.epoch}</h3>
      <dl>
        <dt>Total weekly pool</dt>
        <dd>{formatAEVO(budget.pool_units)} AEVO</dd>
        <dt>Maker pool · 25%</dt>
        <dd>{formatAEVO(budget.maker_pool_units)} AEVO</dd>
        <dt>Trading base, including rounding</dt>
        <dd>{formatAEVO(budget.trading_base_units)} AEVO</dd>
        <dt>Allocation rounding to trading</dt>
        <dd>{formatAEVO(budget.allocation_rounding_to_trading_units)} AEVO</dd>
      </dl>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Allocation</th>
              <th>Reserved AEVO</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(budget.allocations).map(([id, units]) => (
              <tr key={id}>
                <td>{id}</td>
                <td>{formatAEVO(units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>Reserved budgets are not individual awards or claimable balances.</p>
    </div>
  );
}

function AllocationSummary({ allocation }: { allocation: AllocationPreview }) {
  return (
    <div>
      <h3>Allocation estimate · {allocation.id}</h3>
      <dl>
        <dt>Reserved budget</dt>
        <dd>{formatAEVO(allocation.token_units)} AEVO</dd>
        <dt>Proposed awards</dt>
        <dd>{formatAEVO(allocation.paid_units)} AEVO</dd>
        <dt>Proposed return to trading</dt>
        <dd>{formatAEVO(allocation.returned_units)} AEVO</dd>
        <dt>Unallocated</dt>
        <dd>{formatAEVO(allocation.unallocated_units)} AEVO</dd>
      </dl>
      <p>
        Unpublished testnet estimate. The 25% family cap applies within this
        allocation.
      </p>
      {!!allocation.awards?.length && (
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Account family</th>
                <th>Proposed AEVO</th>
                <th>Cap excess (smallest token units, exact)</th>
              </tr>
            </thead>
            <tbody>
              {allocation.awards.map((award) => (
                <tr key={award.account_group}>
                  <td>{award.account_group}</td>
                  <td>{formatAEVO(award.units)}</td>
                  <td>{award.cap_excess}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function hourlyStatus(hour: HourlyProgress) {
  if (hour.Observed === 0) return "No valid measurements";
  return hour.Qualified ? "Qualified" : "Below threshold";
}

function HourlyPresence({
  progress,
  policy,
}: {
  progress: AccountProgress[];
  policy?: MakerLaunchPolicy;
}) {
  return (
    <div>
      <h3>Presence by agreed hour</h3>
      <p>
        Each hour requires{" "}
        {fractionPercent(policy?.hourly_presence_fraction || "")} presence. A
        failed hour excludes only that hour&apos;s score; other qualified hours
        still contribute to the weekly score. Operational checks can still block
        settlement.
      </p>
      {progress.map((account) => (
        <details key={account.Account} open>
          <summary>
            {account.Account} · {account.QualifiedHours} qualified /{" "}
            {account.UnqualifiedHours} unqualified hours
          </summary>
          <p>
            Score from qualified hours: {account.EligibleScore ?? "Unavailable"}
          </p>
          <p>Raw quote score: {account.Score ?? "Unavailable"}</p>
          <p>
            Agreed minimum raw depth per side (USD, exact):{" "}
            {account.CommitmentMinimumDepthUSD ?? "Unavailable"}
          </p>
          <h4>Weekly volatile checks</h4>
          <dl>
            <dt>Valid volatile checks</dt>
            <dd>{account.VolatileObserved ?? "Unavailable"}</dd>
            <dt>Present volatile checks</dt>
            <dd>{account.VolatilePresent ?? "Unavailable"}</dd>
            <dt>Unweighted volatile presence</dt>
            <dd>
              {account.VolatilePresence === undefined
                ? "Unavailable"
                : fractionPercent(account.VolatilePresence)}
            </dd>
            <dt>Volatility status</dt>
            <dd>
              {volatilityStatus(account, policy?.minimum_volatile_checks)}
            </dd>
          </dl>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Hour (UTC)</th>
                  <th>Expected</th>
                  <th>Observed</th>
                  <th>Missing</th>
                  <th>Present</th>
                  <th>Presence</th>
                  <th>Hour status</th>
                </tr>
              </thead>
              <tbody>
                {(account.Hours || []).map((hour) => (
                  <tr key={hour.HourStart}>
                    <td>{hour.HourStart}</td>
                    <td>{hour.Expected}</td>
                    <td>{hour.Observed}</td>
                    <td>{hour.Missing}</td>
                    <td>{hour.Present}</td>
                    <td>{fractionPercent(hour.Presence)}</td>
                    <td>{hourlyStatus(hour)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
}

function volatilityStatus(account: AccountProgress, minimum?: number) {
  if (
    account.VolatilityRequirementApplied === undefined ||
    minimum === undefined
  )
    return "Not available";
  if (!account.VolatilityRequirementApplied)
    return `Waived · fewer than ${minimum} valid volatile checks`;
  return account.VolatilityQualified ? "Qualified" : "Below volatile threshold";
}

function RewardSummary({ payload, manifest }: { payload: any; manifest: any }) {
  return (
    <div>
      <p>
        <strong>Week {payload?.week_id}</strong> ·{" "}
        {(payload?.allocations || []).map((a: any) => a.id).join(", ")}
      </p>
      <dl>
        <dt>Total budget</dt>
        <dd>{formatAEVO(payload?.pool_units || "")} AEVO</dd>
        <dt>Trading base</dt>
        <dd>{formatAEVO(payload?.trading_base_units || "")} AEVO</dd>
        <dt>Proposed maker rewards</dt>
        <dd>{formatAEVO(manifest?.maker_paid_units || "")} AEVO</dd>
        <dt>Returned to trading</dt>
        <dd>{formatAEVO(manifest?.returned_to_trading_units || "")} AEVO</dd>
      </dl>
      {Object.keys(manifest?.awards || {}).length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Account group</th>
                <th>Proposed AEVO</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(manifest.awards).map(([account, units]) => (
                <tr key={account}>
                  <td>{account}</td>
                  <td>{formatAEVO(String(units))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
